import type { Model } from '../../../data/models'
import { userSettingParam, type GenerateSettings } from '../hooks/useGenerateSettings'
import type { UploadedFile, UploadedVideo } from './uploadHelpers'
import { durationLock, isSeedance25Model, trendVideoFollows, videoIntentOf } from './referenceLimits'
import { effectiveOutputFormat } from './modelParams'
import { getScene, sceneCharacter } from '../../../api/sceneSelection'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

interface ImageInputContext {
	requiresTwoImages: boolean
	supportsMultipleImages: boolean
	uploadedImage: UploadedFile | null
	swapImage: UploadedFile | null
	referenceImages: UploadedFile[]
}

// Если aspect_ratio = match_input_image — не отправляем его,
// бэкенд сам определит соотношение из загруженного изображения
function resolveAspectRatio(aspectRatio: string): string | undefined {
	return aspectRatio === 'match_input_image' ? undefined : aspectRatio
}

export function getImageInput(ctx: ImageInputContext): string[] | undefined {
	const { requiresTwoImages, supportsMultipleImages, uploadedImage, swapImage, referenceImages } =
		ctx
	if (requiresTwoImages && uploadedImage?.url && swapImage?.url)
		return [uploadedImage.url, swapImage.url]
	if (supportsMultipleImages && referenceImages.length > 0)
		return referenceImages.filter((img) => img.url).map((img) => img.url!)
	if (uploadedImage?.url) return [uploadedImage.url]
	return undefined
}

export function buildMidjourneyVideoPayload(prompt: string, uploadedImage: UploadedFile) {
	return {
		prompt: prompt.trim(),
		image_url: uploadedImage.url!,
	}
}

export function buildMidjourneyI2IPayload(
	prompt: string,
	uploadedImage: UploadedFile,
	settings: GenerateSettings,
) {
	return {
		prompt: prompt.trim(),
		provider: 'midjourney' as const,
		image_url: uploadedImage.url!,
		aspect_ratio: settings.aspectRatio,
		version: settings.version,
		speed: settings.speed,
		stylization: settings.stylization,
	}
}

export function buildMidjourneyPayload(prompt: string, settings: GenerateSettings, model: Model) {
	return {
		prompt: prompt.trim(),
		task_type: model.backendModel,
		aspect_ratio: resolveAspectRatio(settings.aspectRatio),
		version: settings.version,
		speed: settings.speed,
		stylization: settings.stylization,
	}
}

/** Kling 3.0 в любой поставке (KIE `kling-3.0/video`, прямой `kling-3.0-direct`):
 *  кадры first/last и мультишот — только у неё, 2.6 и motion-control их не знают. */
export function isKling3Model(model: Model): boolean {
	return (model.backendModel || model.id || '').toLowerCase().includes('kling-3')
}

export function buildKlingPayload(
	prompt: string,
	settings: GenerateSettings,
	model: Model,
	uploadedImage: UploadedFile | null,
	uploadedVideo: UploadedVideo | null,
	selectedVideoDuration: number,
	requiresVideo: boolean,
) {
	const actualDuration = uploadedVideo?.url ? String(selectedVideoDuration) : settings.duration
	return {
		prompt: prompt.trim(),
		provider: model.adapter || 'kling',
		model: model.backendModel,
		image_urls: uploadedImage?.url ? [uploadedImage.url] : undefined,
		// Референс карточки уходит ФЛАГОМ: ссылку сервер знает сам (19.09).
		video_urls: uploadedVideo?.url && !uploadedVideo.cardRef ? [uploadedVideo.url] : undefined,
		card_ref_video: uploadedVideo?.cardRef || undefined,
		duration: actualDuration,
		aspect_ratio: resolveAspectRatio(settings.aspectRatio),
		sound: settings.sound,
		resolution: settings.resolution,
		generate_audio: settings.sound,
		character_orientation: requiresVideo ? settings.characterOrientation : undefined,
		keep_original_sound: requiresVideo ? settings.keepOriginalSound : undefined,
		...userChangedField(settings),
	}
}

// Откуда видео в запросе: свои ролики (ссылки) и видео тренда (флаг — ссылку подставит сервер по source_card_id).
// Одно место и для запроса, и для цены (плата за секунды входного видео считает оба).
function videoSources(
	model: Model,
	settings: GenerateSettings,
	uploadedVideo: UploadedVideo | null,
	referenceVideo: UploadedVideo | null,
	referenceVideos?: UploadedVideo[],
) {
	// Референс «под капотом» (19.09): канал занят админским файлом, но в запрос уходит
	// ФЛАГ, а не ссылка — её сервер подставляет сам по source_card_id (card_references).
	// Ссылка в канале — короткая подпись только на просмотр; отправлять её провайдеру
	// нельзя (протухнет в очереди) и незачем (её и прятали от чужих глаз).
	// Открытое видео тренда, которое человек не снимал, уходит и тогда, когда превью ещё не встало в слот (ссылка на просмотр
	// грузится или не загрузилась): виджет уже пишет «Формат и длина — как у видео тренда» — сервер обязан его подставить.
	const trendVideoKept =
		!!settings.cardTrendVideo && !settings.cardTrendVideo.hidden && trendVideoFollows(model, settings, referenceVideos ?? [])
	const cardRefVideo =
		!!uploadedVideo?.cardRef || !!referenceVideo?.cardRef || !!referenceVideos?.some((v) => v.cardRef) || trendVideoKept
	const ownVideos = referenceVideos?.filter((v) => !v.cardRef)
	const multiVideoUrls =
		ownVideos && ownVideos.length > 0
			? ownVideos.filter((v) => v.url).map((v) => v.url!)
			: undefined
	const ownUploaded = uploadedVideo?.cardRef ? undefined : uploadedVideo
	const ownReference = referenceVideo?.cardRef ? undefined : referenceVideo
	const videoUrls =
		multiVideoUrls && multiVideoUrls.length > 0
			? multiVideoUrls
			: ownUploaded?.url || ownReference?.url
				? ([ownUploaded?.url || ownReference?.url].filter(Boolean) as string[])
				: undefined
	return { cardRefVideo, ownVideos, videoUrls }
}

/** Какие видео уйдут в запрос и, значит, оплачиваются как вход (backend services/input_video_billing): свои ссылки и
 *  видео тренда — открытое по флагу или скрытое (запертое админом: сервер подставит его сам). Seedance в «Кадрах»
 *  видео не берёт. */
export function videoInputsOf(
	model: Model,
	settings: GenerateSettings,
	uploadedVideo: UploadedVideo | null,
	referenceVideo: UploadedVideo | null,
	referenceVideos?: UploadedVideo[],
): { ownUrls: string[]; trendVideo: boolean } {
	const { cardRefVideo, videoUrls } = videoSources(model, settings, uploadedVideo, referenceVideo, referenceVideos)
	const frameRefModes = hasFrameRefModes(model)
	if (frameRefModes && (settings.seedanceMode ?? 'frames') === 'frames') return { ownUrls: [], trendVideo: false }
	return { ownUrls: videoUrls ?? [], trendVideo: cardRefVideo || !!settings.cardTrendVideo?.hidden }
}

/** Входные видео запроса → что мерить на сервере и что уже известно: ролик с billedSeconds («Повторить движение» отдаст
 *  отрезок ровно этой длины) не меряем — его секунды известны; остальные меряет сервер (useInputVideoSeconds). */
export function splitKnownInputSeconds(
	ownUrls: string[],
	videos: (UploadedVideo | null | undefined)[],
): { measure: string[]; known: number[] } {
	const billed = new Map<string, number>()
	for (const v of videos) if (v?.url && Number(v.billedSeconds) > 0) billed.set(v.url, Number(v.billedSeconds))
	const measure: string[] = []
	const known: number[] = []
	for (const url of ownUrls) {
		const sec = billed.get(url)
		if (sec != null) known.push(sec)
		else measure.push(url)
	}
	return { measure, known }
}

export function buildVideoPayload(
	prompt: string,
	settings: GenerateSettings,
	model: Model,
	imageInput: string[] | undefined,
	uploadedVideo: UploadedVideo | null,
	referenceVideo: UploadedVideo | null,
	endImage: UploadedFile | null,
	omniReferenceImages: UploadedFile[],
	multiShots: { prompt: string; duration: number }[],
	selectedVideoDuration: number,
	requiresVideo: boolean,
	referenceVideos?: UploadedVideo[],
	referenceAudios?: UploadedFile[],
	expectedCredits?: number | null,
) {
	// Seedance 2.5 с одним видео-референсом: длина ролика = длина референса (utils/referenceLimits);
	// та же цифра стоит в пилюле и в цене, сервер сверит её с файлом ещё раз. Тренд с замком длительности —
	// длина тренда (сервер другую не примет).
	const followSec = durationLock(model, settings, referenceVideos ?? [], requiresVideo && !!uploadedVideo?.url)?.sec ?? null
	const actualDuration = followSec != null
		? String(followSec)
		: requiresVideo && uploadedVideo?.url ? String(selectedVideoDuration) : settings.duration

	// «Повторить движение» (13.10): фото людей по слотам, слева направо — вместо каналов виджета (у замены человека канал
	// один, а людей может быть до трёх). Порядок фото = порядок людей в промпте и в подсказке «кто кого».
	const motionPhotos = settings.motionPhotos?.filter(Boolean)
	if (motionPhotos?.length) imageInput = motionPhotos
	const { cardRefVideo, ownVideos, videoUrls } = videoSources(model, settings, uploadedVideo, referenceVideo, referenceVideos)
	const cardRefAudio = !!referenceAudios?.some((a) => a.cardRef)

	const ownAudios = referenceAudios?.filter((a) => !a.cardRef)
	const audioUrls =
		ownAudios && ownAudios.length > 0
			? ownAudios.filter((a) => a.url).map((a) => a.url!)
			: undefined

	const frameRefModes = hasFrameRefModes(model)
	const isKling3 = isKling3Model(model)
	let firstFrameUrl: string | undefined
	let lastFrameUrl: string | undefined
	let imageUrlsOut: string[] | undefined = imageInput
	let referenceImageUrlsOut: string[] | undefined = omniReferenceImages
		.filter((i) => i.url)
		.map((i) => i.url!)

	if (isKling3) {
		const frames = [...(imageInput || [])]
		if (endImage?.url) frames.push(endImage.url)
		imageUrlsOut = frames.length ? frames : undefined
	}

	const seedanceMode = settings.seedanceMode ?? 'frames'
	if (frameRefModes) {
		const max = model.maxImages ?? 9
		if (seedanceMode === 'frames') {
			firstFrameUrl = imageInput?.[0]
			lastFrameUrl = imageInput?.[1] || endImage?.url
			imageUrlsOut = undefined
			referenceImageUrlsOut = undefined
		} else {
			referenceImageUrlsOut = imageInput?.slice(0, max)
			imageUrlsOut = undefined
			firstFrameUrl = undefined
			lastFrameUrl = undefined
		}
	}

	const videoUrlsOut = frameRefModes && seedanceMode === 'frames' ? undefined : videoUrls
	const audioUrlsOut = frameRefModes && seedanceMode === 'frames' ? undefined : audioUrls
	// Намерение при видео-референсе (Seedance 2.5, utils/referenceLimits): свои ролики —
	// выбор юзера, референс карточки «под капотом» — всегда «edit» (длину делает вход,
	// как и работало). Сервер по нему решает, чем платить и что слать провайдеру.
	const videoIntent =
		frameRefModes && seedanceMode !== 'frames' && (videoUrlsOut?.length || cardRefVideo)
			? (ownVideos?.length ? videoIntentOf(settings) : 'edit')
			: undefined

	const isHeygen = (model.backendModel || '').toLowerCase().includes('heygen')

	// Черновик Seedance 2.5 — проба в 480p (backend: services/seedance_draft): уходит, только если включён тумблер и в запросе
	// нет того, что BytePlus в черновик не принимает (видео, аудио, референс «под капотом» карточки). Разрешение у него одно,
	// что бы ни стояло в настройках.
	const draft =
		settings.draft === true &&
		isSeedance25Model(model) &&
		!videoUrlsOut?.length &&
		!audioUrlsOut?.length &&
		!(frameRefModes && seedanceMode === 'frames' ? false : cardRefVideo) &&
		!(frameRefModes && seedanceMode === 'frames' ? false : cardRefAudio)

	// Фото ассетов сцены лежат среди референсов вслед за фото юзера (useSceneApply): их номера уходят
	// серверу — упоминание @Анна становится «Анна (Image 3, Image 4)», а у моделей с @-синтаксисом —
	// «@Image3, @Image4» (utils/asset_refs + prompt_refs). Фраза «the character reference photos» старых
	// промптов — по номерам первого персонажа сцены.
	const characterRefRanks = characterRanksIn(referenceImageUrlsOut)
	const assetRefRanks = sceneRanksIn(referenceImageUrlsOut)

	return {
		prompt: prompt.trim(),
		provider: model.provider,
		model: model.backendModel,
		image_urls: imageUrlsOut,
		video_urls: videoUrlsOut,
		audio_urls: audioUrlsOut,
		// Флаги референса источника — вместо ссылок (см. выше). Seedance в «Кадрах» от видео тренда отказывается ЯВНО (false):
		// фото там кадр, видео модель не возьмёт — сервер не подставит даже скрытое (shared/trend-video, 12.10).
		card_ref_video: frameRefModes && seedanceMode === 'frames' ? false : cardRefVideo || undefined,
		card_ref_audio: (frameRefModes && seedanceMode === 'frames' ? false : cardRefAudio) || undefined,
		first_frame_url: firstFrameUrl,
		last_frame_url: lastFrameUrl,
		duration: actualDuration,
		aspect_ratio: resolveAspectRatio(settings.aspectRatio),
		resolution: draft ? '480p' : settings.resolution,
		character_orientation: requiresVideo ? settings.characterOrientation : undefined,
		keep_original_sound: requiresVideo
			? settings.keepOriginalSound
			: referenceVideo?.url
				? settings.keepOriginalSound
				: undefined,
		prompt_optimizer: model.supportsPromptOptimizer ? settings.promptOptimizer : undefined,
		sound: settings.sound,
		generate_audio:
			model.supportsAudio || model.supportsGenerateAudio ? settings.sound : undefined,
		negative_prompt: settings.negativePrompt || undefined,
		end_image_url: frameRefModes || isKling3 ? undefined : endImage?.url || undefined,
		reference_image_urls:
			referenceImageUrlsOut && referenceImageUrlsOut.length ? referenceImageUrlsOut : undefined,
		video_reference_type: referenceVideo?.url ? settings.videoReferenceType : undefined,
		video_intent: videoIntent,
		character_ref_ranks: characterRefRanks.length ? characterRefRanks : undefined,
		asset_ref_ranks: Object.keys(assetRefRanks).length ? assetRefRanks : undefined,
		multi_prompt: multiShots.length ? JSON.stringify(multiShots) : undefined,
		multi_shots: isKling3 && multiShots.length > 0 ? true : undefined,
		web_search: model.supportsWebSearch ? settings.webSearch : undefined,
		nsfw_checker: model.supportsNsfwChecker ? settings.nsfwChecker : undefined,
		draft: draft || undefined,
		// Цена с кнопки: при плате за секунды входного видео сервер не спишет больше показанного (input_video_billing).
		expected_credits: expectedCredits != null && expectedCredits > 0 ? expectedCredits : undefined,
		replace_instruction: settings.motionInstruction || undefined,
		...userChangedField(settings),

		// ── HeyGen-specific (пропускаем undefined чтобы backend не получал шум) ──
		avatar_id: isHeygen ? settings.heygenAvatarId || undefined : undefined,
		voice_id: isHeygen ? settings.heygenVoiceId || undefined : undefined,
		avatar_style: isHeygen ? settings.heygenAvatarStyle || undefined : undefined,
		voice_speed: isHeygen ? settings.heygenVoiceSpeed ?? undefined : undefined,
		voice_emotion: isHeygen ? settings.heygenVoiceEmotion || undefined : undefined,
		caption: isHeygen ? settings.heygenCaption ?? undefined : undefined,
		video_url: isHeygen ? settings.heygenVideoUrl || undefined : undefined,
		output_language: isHeygen ? settings.heygenOutputLanguage || undefined : undefined,
		enable_dynamic_duration: isHeygen ? settings.heygenEnableDynamicDuration ?? undefined : undefined,
		translate_audio_only: isHeygen ? settings.heygenTranslateAudioOnly ?? undefined : undefined,
		title: isHeygen ? settings.heygenTitle || undefined : undefined,
	}
}

export function buildFluxPayload(
	prompt: string,
	settings: GenerateSettings,
	model: Model,
	imageInput: string[] | undefined,
) {
	return {
		prompt: prompt.trim(),
		provider: 'flux' as const,
		model: model.backendModel,
		aspect_ratio: resolveAspectRatio(settings.aspectRatio),
		resolution: settings.resolution,
		image_input: imageInput,
		...assetRanksField(imageInput),
		...userChangedField(settings),
	}
}

export function buildPresentationPayload(
	prompt: string,
	model: Model,
	fileUrl: string,
) {
	return {
		model: model.backendModel,
		prompt: prompt.trim(),
		file_url: fileUrl,
	}
}

/** Какие настройки тренда пользователь поменял сам (settings.userChanged) — полями запроса. Уходит всегда: пустой
 *  список значит «ничего не менял», отсутствие — старый виджет (backend services/card_check, 11.10). */
export function userChangedField(settings: GenerateSettings): { user_changed: string[] } {
	const out = (settings.userChanged ?? [])
		.map((key) => userSettingParam(key))
		.filter((v): v is string => !!v)
	return { user_changed: [...new Set(out)] }
}

export function buildImagePayload(
	prompt: string,
	settings: GenerateSettings,
	model: Model,
	imageInput: string[] | undefined,
) {
	return {
		prompt: prompt.trim(),
		provider: model.provider,
		model: model.backendModel,
		aspect_ratio: resolveAspectRatio(settings.aspectRatio),
		resolution: settings.resolution,
		image_input: imageInput,
		...assetRanksField(imageInput),
		...(model.backendModel?.includes('imagen')
			? { safety_filter_level: settings.safetyFilter, output_format: effectiveOutputFormat(model, settings.outputFormat) }
			: model.provider === 'nano_banana' || model.supportsOutputFormat
				? { output_format: effectiveOutputFormat(model, settings.outputFormat) }
				: {}),
		...(model.supportsNegativePrompt && settings.negativePrompt
			? { negative_prompt: settings.negativePrompt }
			: {}),
		...(model.supportsGoogleSearch && settings.googleSearch ? { google_search: true } : {}),
		...(model.qualityLevels && settings.quality ? { quality: settings.quality } : {}),
		...userChangedField(settings),
	}
}

/** Номера (1-based) фото ассетов сцены среди нумеруемых картинок запроса: {id ассета: [n…]} → asset_ref_ranks.
 *  Сверка по path (подпись presigned меняется, путь стабилен); фото не в списке (первый кадр видео) — без номера. */
export function sceneRanksIn(numbered: string[] | undefined): Record<string, number[]> {
	const sel = getScene()
	const out: Record<string, number[]> = {}
	if (!sel || !numbered?.length) return out
	const idx = numbered.map((u) => u.split('?', 1)[0])
	for (const [id, urls] of Object.entries(sel.placedBy)) {
		const ranks = urls.map((u) => idx.indexOf(u.split('?', 1)[0]) + 1).filter((n) => n > 0)
		if (ranks.length) out[id] = ranks
	}
	return out
}

const assetRanksField = (numbered: string[] | undefined) => {
	const ranks = sceneRanksIn(numbered)
	return Object.keys(ranks).length ? { asset_ref_ranks: ranks } : {}
}

/** Номера фото первого персонажа сцены среди референсов — для фразы «the character reference photos». */
export function characterRanksIn(referenceUrls: string[] | undefined): number[] {
	const hero = sceneCharacter(getScene())
	return hero ? sceneRanksIn(referenceUrls)[hero.id] ?? [] : []
}
